import { useState } from 'react';
import {
  streakLevels,
  streakUsers,
  streakStats,
  streakRules,
  activityConfigs,
  rewardConfigs,
  getTopStreaks,
  getUsersAtRisk,
  getLevelDistribution,
  type StreakLevel,
  type ActivityType,
} from '@/mocks/rfwStreaks';

export default function RachasSection() {
  const [activeTab, setActiveTab] = useState<'rachas' | 'actividades' | 'recompensas' | 'reglas'>('rachas');
  const [selectedLevel, setSelectedLevel] = useState<StreakLevel | 'todas'>('todas');
  const [selectedActivity, setSelectedActivity] = useState<ActivityType | 'todas'>('todas');

  const topStreaks = getTopStreaks(6);
  const atRiskUsers = getUsersAtRisk();
  const levelDist = getLevelDistribution();

  const filteredUsers = (() => {
    let users = [...streakUsers];
    if (selectedLevel !== 'todas') {
      users = users.filter((u) => u.currentLevel === selectedLevel);
    }
    if (selectedActivity !== 'todas') {
      users = users.filter((u) => u.activeActivities.includes(selectedActivity));
    }
    return users.slice(0, 6);
  })();

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-100 border border-fuchsia-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-fuchsia-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-fuchsia-700 tracking-wide">SISTEMA DE RACHAS</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Tu constancia tiene <span className="bg-gradient-to-r from-amber-500 via-red-500 to-fuchsia-500 bg-clip-text text-transparent">recompensa</span>
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Cada día que interactuás con RFW suma a tu racha. Mientras más días seguidos, más puntos, insignias y beneficios desbloqueás. Y si hay fallos técnicos, tu racha está protegida.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-8">
          {[
            { value: streakStats.totalActiveStreaks.toLocaleString(), label: 'Rachas Activas', icon: 'ri-fire-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { value: streakStats.longestActiveStreak.toString(), label: 'Racha Más Larga', icon: 'ri-trophy-line', color: 'text-fuchsia-600', bg: 'bg-fuchsia-50' },
            { value: streakStats.todayCompleted.toLocaleString(), label: 'Completaron Hoy', icon: 'ri-check-double-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { value: streakStats.levelUpsToday.toLocaleString(), label: 'Subieron Nivel Hoy', icon: 'ri-arrow-up-line', color: 'text-sky-600', bg: 'bg-sky-50' },
            { value: streakStats.streaksAtRisk.toLocaleString(), label: 'En Riesgo', icon: 'ri-alert-line', color: 'text-rose-600', bg: 'bg-rose-50' },
          ].map((stat, i) => (
            <div key={i} className={`${stat.bg} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
              <div className="flex items-center justify-center gap-1 text-xs text-foreground-500 mt-1">
                <i className={`${stat.icon} text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            {(['rachas', 'actividades', 'recompensas', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'rachas' && 'Rachas Activas'}
                {tab === 'actividades' && 'Actividades'}
                {tab === 'recompensas' && 'Recompensas'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Rachas Activas */}
        {activeTab === 'rachas' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-foreground-500 mr-1">Nivel:</span>
                {(['todas', 1, 3, 7, 15, 30, 100] as const).map((lvl) => {
                  const cfg = lvl === 'todas' ? null : streakLevels.find((s) => s.days === lvl);
                  return (
                    <button
                      key={lvl}
                      onClick={() => setSelectedLevel(lvl === 'todas' ? 'todas' : lvl)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                        selectedLevel === lvl
                          ? 'bg-fuchsia-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {lvl === 'todas' ? 'Todas' : cfg?.label ?? `${lvl} Días`}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-foreground-500 mr-1">Actividad:</span>
                {(['todas', ...activityConfigs.map((a) => a.key)] as const).map((act) => {
                  const cfg = act === 'todas' ? null : activityConfigs.find((a) => a.key === act);
                  return (
                    <button
                      key={act}
                      onClick={() => setSelectedActivity(act === 'todas' ? 'todas' : act)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                        selectedActivity === act
                          ? 'bg-sky-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cfg?.icon && <i className={`${cfg.icon} mr-1`}></i>}
                      {act === 'todas' ? 'Todas' : cfg?.label ?? act}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map((user) => {
                const levelCfg = streakLevels.find((l) => l.days === user.currentLevel);
                return (
                  <div
                    key={user.id}
                    className={`bg-background-50 rounded-lg p-4 border ${
                      user.riskOfLosing ? 'border-rose-200' : 'border-background-200/70'
                    } hover:border-fuchsia-200 transition-colors`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
                        {user.todayCompleted && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-foreground-950 truncate">{user.name}</div>
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                          <span>Nivel {user.level}</span>
                          {user.isVip && <span className="px-1.5 py-0.5 bg-fuchsia-100 text-fuchsia-700 rounded-full text-xs font-medium">VIP</span>}
                        </div>
                      </div>
                      <div className={`flex flex-col items-center ${levelCfg?.color ?? 'text-amber-500'}`}>
                        <div className="flex items-center gap-0.5">
                          <i className={`${levelCfg?.icon ?? 'ri-fire-line'} text-lg`}></i>
                          <span className="text-lg font-bold">{user.currentStreak}</span>
                        </div>
                        <span className="text-xs text-foreground-400">días</span>
                      </div>
                    </div>

                    {/* Level bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-foreground-500 mb-1">
                        <span>{levelCfg?.label ?? 'Inicio'}</span>
                        <span>{user.nextLevel} días</span>
                      </div>
                      <div className="w-full h-1.5 bg-background-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${levelCfg?.gradient ?? 'from-amber-400 to-red-400'} rounded-full transition-all`}
                          style={{ width: `${Math.min(100, (user.currentStreak / user.nextLevel) * 100)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-foreground-400 mt-1">Faltan {user.daysUntilNextLevel} días para {user.nextLevel} días</div>
                    </div>

                    {/* Activities */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {user.activeActivities.map((act) => {
                        const cfg = activityConfigs.find((a) => a.key === act);
                        return (
                          <span key={act} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.bgColor ?? 'bg-background-100'} ${cfg?.color ?? 'text-foreground-600'}`}>
                            <i className={`${cfg?.icon ?? 'ri-check-line'} text-xs`}></i>
                            {cfg?.label ?? act}
                          </span>
                        );
                      })}
                    </div>

                    {/* Badges */}
                    {user.badgesUnlocked.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {user.badgesUnlocked.map((badge) => (
                          <span key={badge} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium border border-amber-200">
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}

                    {user.riskOfLosing && (
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-rose-600 bg-rose-50 rounded-md px-2.5 py-1.5">
                        <i className="ri-alert-line"></i>
                        <span>¡Completá hoy o perdés tu racha!</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Actividades */}
        {activeTab === 'actividades' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              {activityConfigs.map((act) => (
                <div key={act.key} className="bg-background-50 rounded-lg p-4 border border-background-200/70 text-center hover:border-fuchsia-200 transition-colors">
                  <div className={`w-12 h-12 mx-auto rounded-full ${act.bgColor} flex items-center justify-center mb-3`}>
                    <i className={`${act.icon} text-xl ${act.color}`}></i>
                  </div>
                  <div className={`font-semibold text-sm ${act.color} mb-1`}>{act.label}</div>
                  <div className="text-xs text-foreground-500 mb-2">{act.description}</div>
                  <div className="flex items-center justify-center gap-2 text-xs text-foreground-400">
                    <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">+{act.basePoints} pts</span>
                    <span className="bg-fuchsia-50 text-fuchsia-700 px-2 py-0.5 rounded-full">+{act.streakBonus} bonus</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Recompensas */}
        {activeTab === 'recompensas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {rewardConfigs.map((reward) => (
                <div key={reward.key} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-fuchsia-200 transition-colors">
                  <div className={`w-14 h-14 rounded-full ${reward.bgColor} flex items-center justify-center mb-4`}>
                    <i className={`${reward.icon} text-2xl ${reward.color}`}></i>
                  </div>
                  <div className={`font-bold text-base ${reward.color} mb-2`}>{reward.label}</div>
                  <div className="text-sm text-foreground-600 mb-3">{reward.description}</div>
                  <div className="flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className="ri-lock-unlock-line"></i>
                    <span>{reward.unlockAt}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Extra: Level multipliers */}
            <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Multiplicadores por Nivel de Racha</div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {streakLevels.map((lvl) => (
                  <div key={lvl.days} className="text-center bg-background-50 rounded-md p-2 border border-background-200/70">
                    <div className={`text-xs font-semibold ${lvl.color} mb-1`}>{lvl.label}</div>
                    <div className="text-lg font-bold text-foreground-950">x{lvl.rewardMultiplier}</div>
                    <div className="text-xs text-foreground-400">puntos</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {streakRules.map((rule) => (
                <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${rule.bgColor} flex items-center justify-center mb-3`}>
                    <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <div className="text-xs text-foreground-600 leading-relaxed">{rule.description}</div>
                </div>
              ))}
            </div>
            {/* Level distribution */}
            <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Distribución por Nivel</div>
              <div className="flex flex-wrap items-end gap-2 h-24">
                {levelDist.map((d) => {
                  const cfg = streakLevels.find((l) => l.days === d.level);
                  const maxCount = Math.max(...levelDist.map((x) => x.count), 1);
                  const height = (d.count / maxCount) * 80;
                  return (
                    <div key={d.level} className="flex flex-col items-center flex-1 min-w-0">
                      <span className="text-xs font-semibold text-foreground-950">{d.count}</span>
                      <div
                        className={`w-full max-w-[40px] rounded-t-md bg-gradient-to-t ${cfg?.gradient ?? 'from-amber-400 to-red-400'} mt-1 transition-all`}
                        style={{ height: `${height}px` }}
                      ></div>
                      <span className="text-xs text-foreground-400 mt-1">{d.level}d</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/rachas"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-fuchsia-500 text-white rounded-full text-sm font-semibold hover:bg-fuchsia-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-fire-line"></i>
            Ver todas las rachas
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">No perder racha por fallos técnicos.</p>
        </div>
      </div>
    </section>
  );
}